import type { SiteRagReport } from '../types/siteRag';

/**
 * Firestore owns these fields after a report body has been written. They are
 * deliberately narrow: spreading an entire metadata document over the GCS
 * body can replace semantic content with stale list-card fields, while
 * spreading the body last loses artifact pointers committed after the body.
 */
export const AUTHORITATIVE_REPORT_METADATA_FIELDS = [
  'id',
  'siteId',
  'storagePath',
  'reportRevision',
  'pdfDelivery',
  'reportRevisionId',
  'reportContentRevision',
  'reportContentRevisionId',
  'reportContentInputHash',
  'reportContentHash',
  'reportContentReadiness',
  'reportContentState',
  'analysisRevision',
  'analysisRevisionId',
  'analysisInputHash',
  'analysisContentHash',
  'analysisReadiness',
  'analysisState',
  'analysisRevisions',
  'artifactState',
  'deliveryDecision',
  'sourcePath',
  'sourceHash',
  'documentPath',
  'presentationPath',
  'layoutManifestPath',
  'projectionManifestPaths',
  'artifactManifestPath',
  'legacyUserStateBodyAvailable',
  'requesterUid',
  'siteOwnerUid',
  'reportOwnerUid',
  'lineageId',
  'parentReportId',
  'companyId',
  'isCompanyShared',
  // Transient owner namespace derived from an authorized collection-group
  // result. It is required for subsequent body reads, but is stripped before
  // every write by reportGcsSerialization.
  '_ownerUid',
] as const;

export type AuthoritativeReportMetadataField =
  (typeof AUTHORITATIVE_REPORT_METADATA_FIELDS)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Validate the common semantic envelope shared by all 11 report types. Some
 * non-scored types legitimately omit `categories`, so that field alone cannot
 * be the discriminator. Firestore list metadata lacks narrative, location,
 * and the three core content arrays and therefore cannot pass this boundary.
 */
export function isRenderableReportBody(
  value: unknown,
): value is Record<string, unknown> & {
  id: string;
  siteName: string;
  address: string;
  summary: string;
  location: Record<string, unknown>;
  timeSeries: unknown[];
  risks: unknown[];
  recommendations: unknown[];
} {
  if (!isRecord(value) || !isRecord(value.location)) return false;
  return typeof value.id === 'string'
    && value.id.trim().length > 0
    && typeof value.siteName === 'string'
    && typeof value.address === 'string'
    && typeof value.summary === 'string'
    && typeof value.location.lat === 'number'
    && Number.isFinite(value.location.lat)
    && typeof value.location.lng === 'number'
    && Number.isFinite(value.location.lng)
    && typeof value.location.address === 'string'
    && Array.isArray(value.timeSeries)
    && Array.isArray(value.risks)
    && Array.isArray(value.recommendations)
    && (value.categories === undefined || Array.isArray(value.categories));
}

export function pickAuthoritativeReportMetadata(
  metadata: Record<string, unknown>,
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const field of AUTHORITATIVE_REPORT_METADATA_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(metadata, field)) {
      picked[field] = metadata[field];
    }
  }
  return picked;
}

/**
 * Container-shape normalization for `categories`, applied BEFORE the
 * renderability gate on EVERY body-resolution path (hydration AND the direct
 * full-body branch of the print route). Two legitimate bodies arrive with a
 * non-array `categories` container:
 *
 * - keyed map (2026-08-13, Redhead PSR RCA): a 309KB public-sentiment body
 *   whose `categories` serialized as an OBJECT keyed by id — same information,
 *   different container. The gate rejected it and the reader showed "could not
 *   be loaded" forever while /api/storage/load returned 200.
 * - `null` (2026-08-17, Pronghorn exec-summary RCA): the persistence hygiene
 *   pass serializes `undefined → null` (Firestore rejects undefined), so every
 *   non-scored type that legitimately omits `categories` (executive-summary,
 *   custom-brief) stages and persists `categories: null`. The generation-time
 *   print renders the STAGED source directly — no hydration pass — so the
 *   un-normalized null failed `isRenderableReportBody`, the print route 502'd,
 *   and the whole generation was lost (non-domain types have no pre-print
 *   body commit). Deterministic for every executive-summary/custom-brief run.
 *
 * Coerce rather than refuse the body over one field's container shape. A
 * non-record, non-array `categories` (string/number) is dropped to undefined —
 * the same forgiveness the hydration path has applied since 2026-08-13.
 */
export function normalizeReportBodyContainerShapes(body: unknown): unknown {
  if (!isRecord(body)) return body;
  if (body.categories === undefined || Array.isArray(body.categories)) return body;
  return {
    ...body,
    categories: isRecord(body.categories) ? Object.values(body.categories) : undefined,
  };
}

/**
 * Hydrate semantic content from GCS while retaining only server-authoritative
 * Firestore identity, ownership, lineage, delivery, and artifact fields.
 */
export function mergeReportBodyWithAuthoritativeMetadata(
  body: unknown,
  metadata: Record<string, unknown>,
): SiteRagReport | null {
  const candidate = normalizeReportBodyContainerShapes(body);
  if (!isRenderableReportBody(candidate)) return null;
  // A later integrity/quality hold in metadata must survive loading an older
  // immutable body. Conversely, null metadata must never clear a body hold.
  // Clearing either requires its own evidence-bound persistence operation.
  const metadataHeld = metadata.qualityHold === 'needs-revision';
  const metadataHasHold = Boolean(metadata.qualityHold);
  const metadataFindings = metadata.qualityHoldFindings;
  const metadataHasFindings = metadataFindings != null
    && (!Array.isArray(metadataFindings) || metadataFindings.length > 0);
  const holdFindings = metadataHasHold || metadataHasFindings
    ? [...new Set([candidate.qualityHoldFindings, metadata.qualityHoldFindings]
      .flatMap(value => Array.isArray(value) ? value : value == null ? [] : [value]))]
    : undefined;
  const next: Record<string, unknown> = {
    ...candidate,
    ...pickAuthoritativeReportMetadata(metadata),
    ...(metadataHeld || (metadataHasHold && !candidate.qualityHold)
      ? { qualityHold: metadata.qualityHold } : {}),
    // Empty metadata must never erase a body's hold findings. A malformed
    // nonempty carrier stays ineligible rather than becoming an empty array.
    ...(holdFindings ? { qualityHoldFindings: holdFindings } : {}),
    _metadataOnly: false,
  };
  if ((candidate as { _portfolioSlimBody?: boolean })._portfolioSlimBody === true) {
    next._portfolioSlimBody = true;
  } else {
    delete next._portfolioSlimBody;
  }
  return next as unknown as SiteRagReport;
}

export function isPortfolioSlimReportBody(report: unknown): boolean {
  return isRecord(report) && report._portfolioSlimBody === true;
}

export function isFullReaderReadyReportBody(report: unknown): boolean {
  return isRecord(report) && report._metadataOnly !== true && report._portfolioSlimBody !== true;
}

/**
 * Apply a delayed hydrated report body to the current in-store row only when:
 *   1. The row still points at the exact artifact the body was read from.
 *   2. An incoming `_portfolioSlimBody` never downgrades an already-full body
 *      (`!_metadataOnly && !_portfolioSlimBody`) or the currently active report.
 */
export function applyHydratedReportBody(
  current: SiteRagReport,
  metadata: Record<string, unknown>,
  hydrated: SiteRagReport,
  options?: { activeReportId?: string | null },
): SiteRagReport {
  const currentMetadata = current as unknown as Record<string, unknown>;
  if (
    current.id !== metadata.id
    || ['storagePath', 'reportRevisionId', 'sourceHash', 'siteId']
      .some((key) => currentMetadata[key] !== metadata[key])
  ) {
    return current;
  }
  if (
    isPortfolioSlimReportBody(hydrated)
    && (
      isFullReaderReadyReportBody(current)
      || (options?.activeReportId != null && current.id === options.activeReportId)
    )
  ) {
    return current;
  }
  return mergeReportBodyWithAuthoritativeMetadata(hydrated, currentMetadata) ?? current;
}

/**
 * Sanity-finding decision fields the USER owns on an otherwise immutable
 * report: the reader's Apply-on-rerun / Ignore / note. Everything else on a
 * finding is generation output.
 */
export const SANITY_DECISION_FIELDS = [
  'resolution',
  'userNote',
  'applyDirective',
  'resolvedAt',
] as const;

/**
 * Where a report's USER-STATE body lives. Immutable-v2 reports keep their
 * generated body at a content-addressed revision path (`storagePath`) that
 * the rules forbid the client to repoint (`preservesServerReportState`), so
 * `updateRagReport` persists reader decisions to the owner-namespace legacy
 * object instead. Nothing read that object back (live 2026-08-18, Redhead
 * feasibility): the reader showed "Will apply on rerun", the rerun hydrated
 * from `storagePath`, saw 18 open findings, and applied nothing.
 */
export function reportUserStateBodyPath(
  metadata: Record<string, unknown>,
  fallbackUid: string,
): string | null {
  const owner = [metadata.reportOwnerUid, metadata._ownerUid, metadata.requesterUid, fallbackUid]
    .find((v): v is string => typeof v === 'string' && v.length > 0);
  const id = typeof metadata.id === 'string' ? metadata.id : null;
  if (!owner || !id) return null;
  return `users/${owner}/reports/${id}.json`;
}

/**
 * First-class identity-keyed report user state shipped at this instant. A
 * revision-backed report's generated body lives at its immutable revision
 * path; a separate flat owner object is possible only when metadata positively
 * records that legacy state. Without that marker the flat probe is a guessed
 * path and routinely 404s, including for older/undated revisions. Legacy flat
 * reports retain the cutover fallback. An explicit metadata marker wins.
 */
export const REPORT_USER_STATE_DOC_CUTOVER = '2026-08-18T16:56:57.000Z' as const;

export function shouldProbeLegacyReportUserState(
  metadata: Record<string, unknown>,
): boolean {
  if (metadata.legacyUserStateBodyAvailable === true) return true;
  if (metadata.legacyUserStateBodyAvailable === false) return false;
  if (isImmutableRevisionReport(metadata)) return false;

  const finalisedAt = [
    metadata.createdAt,
    metadata.reportRevisionCreatedAt,
    isRecord(metadata.reportRevision) ? metadata.reportRevision.createdAt : undefined,
  ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  if (!finalisedAt) return true;
  const timestamp = Date.parse(finalisedAt);
  return !Number.isFinite(timestamp) || timestamp < Date.parse(REPORT_USER_STATE_DOC_CUTOVER);
}

const negativeLegacyUserStatePaths = new Set<string>();

/**
 * Best-effort Layer 1 overlay with a cutover gate and per-session negative
 * cache. `load` returns null for an absent object. Layer 2 Firestore user state
 * remains separate and authoritative at the call sites.
 */
export async function overlayLegacySanityDecisionsIfPlausible<
  T extends { sanityFindings?: unknown },
>(
  base: T,
  metadata: Record<string, unknown>,
  fallbackUid: string,
  load: (path: string) => Promise<unknown | null>,
  onFailure?: (error: unknown) => void,
): Promise<T> {
  if (!shouldProbeLegacyReportUserState(metadata)) return base;
  const path = reportUserStateBodyPath(metadata, fallbackUid);
  if (!path || path === metadata.storagePath || negativeLegacyUserStatePaths.has(path)) return base;
  try {
    const legacy = await load(path);
    if (legacy == null) {
      negativeLegacyUserStatePaths.add(path);
      return base;
    }
    return overlaySanityDecisions(base, legacy);
  } catch (error) {
    negativeLegacyUserStatePaths.add(path);
    onFailure?.(error);
    return base;
  }
}

/** True when the report body is served from an immutable revision object. */
export function isImmutableRevisionReport(metadata: Record<string, unknown>): boolean {
  return typeof metadata.reportRevisionId === 'string' || isRecord(metadata.reportRevision);
}

/**
 * Overlay the user's sanity decisions from the user-state body onto the
 * hydrated (revision) body. Positional, guarded: the user-state array was
 * written as `{...hydratedRevisionBody, sanityFindings: updated}` so it is the
 * SAME array with decision fields added — but a mismatched length or a
 * finding whose text differs at the same index means the two bodies drifted,
 * and then nothing is overlaid (fail closed: a stale decision must never land
 * on a different finding). Returns the input untouched when there is nothing
 * to overlay.
 */
export function overlaySanityDecisions<T extends { sanityFindings?: unknown }>(
  base: T,
  userState: unknown,
): T {
  if (!isRecord(userState) || !Array.isArray(userState.sanityFindings)) return base;
  const baseFindings = Array.isArray(base.sanityFindings) ? base.sanityFindings : null;
  if (!baseFindings || baseFindings.length !== userState.sanityFindings.length) return base;
  const userFindings = userState.sanityFindings as unknown[];
  let changed = false;
  const merged = baseFindings.map((entry, index) => {
    const decided = userFindings[index];
    if (!isRecord(entry) || !isRecord(decided)) return entry;
    if (
      typeof entry.finding === 'string'
      && typeof decided.finding === 'string'
      && entry.finding !== decided.finding
    ) return entry;
    const overlay: Record<string, unknown> = {};
    for (const field of SANITY_DECISION_FIELDS) {
      if (decided[field] !== undefined && entry[field] !== decided[field]) overlay[field] = decided[field];
    }
    if (Object.keys(overlay).length === 0) return entry;
    changed = true;
    return { ...entry, ...overlay };
  });
  return changed ? { ...base, sanityFindings: merged } : base;
}

const METADATA_HEAVY_BODY_FIELDS = [
  'siteContextAtGen',
  'siteContextSnapshot',
  'references',
  'approvedFactualCorrections',
  'appliedCorrectionsSummary',
  'lastAuditCorrectionReceipt',
  'sedText',
  'generationTelemetry',
  'decisionAnalysis',
] as const;

const DOCUMENT_HEAVY_TEXT_FIELDS = [
  'fullExtractedText',
  'textContent',
  'extractedText',
  'content',
  'topicSegments',
  'evidenceDigest',
  'generationScreeningExtraction',
  'extractionOutcome',
] as const;

const QA_FINDINGS_HEAVY_FIELDS = [
  'finalSourceReview',
  'sourceRoleReview',
  'actionClusters',
  'appliedCorrectionsSummary',
  'jevReportReview',
  'reliabilityMarkerCorrections',
  'finalRepairPass',
  'boundedGroundingRepairs',
] as const;

function compactInlineDocumentEntry(entry: unknown): unknown {
  if (!isRecord(entry)) return entry;
  if (!DOCUMENT_HEAVY_TEXT_FIELDS.some((key) => key in entry)) return entry;
  const next = { ...entry };
  for (const key of DOCUMENT_HEAVY_TEXT_FIELDS) {
    delete next[key];
  }
  return next;
}

function compactQaFindingsRecord(qaFindings: unknown): unknown {
  if (!isRecord(qaFindings)) return qaFindings;
  const hasHeavyField = QA_FINDINGS_HEAVY_FIELDS.some((key) => key in qaFindings);
  const caveatHonesty = qaFindings.caveatHonesty;
  const hasHeavyCaveatDowngraded = isRecord(caveatHonesty) && 'downgraded' in caveatHonesty;
  if (!hasHeavyField && !hasHeavyCaveatDowngraded) return qaFindings;
  const next: Record<string, unknown> = { ...qaFindings };
  for (const key of QA_FINDINGS_HEAVY_FIELDS) {
    delete next[key];
  }
  if (isRecord(caveatHonesty) && hasHeavyCaveatDowngraded) {
    const nextCaveat = { ...caveatHonesty };
    delete nextCaveat.downgraded;
    next.caveatHonesty = nextCaveat;
  }
  return next;
}

function compactDecisionAnalysisRecord(decisionAnalysis: unknown): unknown {
  if (!isRecord(decisionAnalysis)) return decisionAnalysis;
  const hasInput = 'input' in decisionAnalysis;
  const semantic = decisionAnalysis.semantic;
  const hasHeavySemantic = isRecord(semantic)
    && ('sections' in semantic || 'sources' in semantic || 'citations' in semantic);
  if (!hasInput && !hasHeavySemantic) return decisionAnalysis;
  const next: Record<string, unknown> = { ...decisionAnalysis };
  delete next.input;
  if (isRecord(semantic) && hasHeavySemantic) {
    const nextSemantic = { ...semantic };
    delete nextSemantic.sections;
    delete nextSemantic.sources;
    delete nextSemantic.citations;
    next.semantic = nextSemantic;
  }
  return next;
}

/**
 * Strip multi-megabyte legacy body-only fields from non-renderable Firestore
 * report metadata rows (`qaFindings` arrays, `references`, `approvedFactualCorrections`,
 * `appliedCorrectionsSummary`, `siteContextAtGen`/`siteContextSnapshot`, inline
 * document text blobs) so retaining 400+ library rows in `siteRag.reports` does
 * not pin tens of megabytes of V8 heap on metadata-only rows. Returns the
 * input reference unchanged when no heavy fields are present or when the row
 * is already a renderable body.
 */
export function compactReportMetadataRow(
  metadata: Record<string, unknown>,
): Record<string, unknown> {
  if (isRenderableReportBody(metadata)) return metadata;
  const hasHeavyField = METADATA_HEAVY_BODY_FIELDS.some((key) => key in metadata);
  const compactedQa = compactQaFindingsRecord(metadata.qaFindings);
  const docs = Array.isArray(metadata.documents) ? metadata.documents : null;
  let compactedDocs: unknown[] | null = null;
  if (docs) {
    for (let i = 0; i < docs.length; i += 1) {
      const nextDoc = compactInlineDocumentEntry(docs[i]);
      if (nextDoc !== docs[i]) {
        if (!compactedDocs) compactedDocs = docs.slice(0, i);
        compactedDocs.push(nextDoc);
      } else if (compactedDocs) {
        compactedDocs.push(docs[i]);
      }
    }
  }
  if (!hasHeavyField && compactedQa === metadata.qaFindings && !compactedDocs) {
    return metadata;
  }
  const next: Record<string, unknown> = { ...metadata };
  for (const key of METADATA_HEAVY_BODY_FIELDS) {
    delete next[key];
  }
  if (compactedQa !== metadata.qaFindings) {
    next.qaFindings = compactedQa;
  }
  if (compactedDocs) {
    next.documents = compactedDocs;
  }
  return next;
}

/**
 * Compact a background-warmed portfolio head body (Phase 2 of `fetchRagReports`)
 * so that all ~50 portfolio heads can remain resident for composite scoring,
 * sidebar recommendation badges, and evidence readiness without pinning
 * ~250–300 MB of reader/editor-only JSON payloads in the browser V8 heap.
 *
 * Marks the returned report with `_portfolioSlimBody: true` so opening the
 * report in the reader (`setActiveRagReport` / `hydrateReportBody`), hovering
 * (`prefetchReport`), or saving (`isReportHydratedForGcsSave`) transparently
 * hydrates the full un-slimmed body on demand.
 */
export function slimPortfolioReportBody(report: SiteRagReport): SiteRagReport {
  const raw = report as unknown as Record<string, unknown>;
  if (raw._portfolioSlimBody === true) return report;
  const scenarioInput =
    isRecord(raw.generationImpactScenarioAtGen) && isRecord(raw.generationImpactScenarioAtGen.input)
      ? raw.generationImpactScenarioAtGen.input
      : null;
  const scenarioUnits =
    scenarioInput && Array.isArray(scenarioInput.units) ? scenarioInput.units : null;
  const keepAllocatedServiceEvidenceBlobs =
    scenarioInput?.gridConnection === 'grid-connected'
    && Boolean(scenarioUnits && scenarioUnits.length > 0)
    && scenarioUnits!.every((u) => isRecord(u) && u.technology === 'grid-import');
  const next: Record<string, unknown> = {
    ...raw,
    _portfolioSlimBody: true,
  };
  delete next.siteContextAtGen;
  delete next.siteContextSnapshot;
  delete next.sedText;
  delete next.approvedFactualCorrections;
  delete next.appliedCorrectionsSummary;
  delete next.lastAuditCorrectionReceipt;
  delete next.generationTelemetry;
  if (!keepAllocatedServiceEvidenceBlobs) {
    delete next.references;
    if (Array.isArray(raw.documents)) {
      next.documents = raw.documents.map((doc) => compactInlineDocumentEntry(doc));
    }
  }
  if ('qaFindings' in raw) {
    next.qaFindings = compactQaFindingsRecord(raw.qaFindings);
  }
  if ('decisionAnalysis' in raw) {
    next.decisionAnalysis = compactDecisionAnalysisRecord(raw.decisionAnalysis);
  }
  return next as unknown as SiteRagReport;
}

/** Explicitly mark a headline-only Firestore document as non-renderable. */
export function markReportMetadataOnly(
  metadata: Record<string, unknown>,
): SiteRagReport {
  return { ...compactReportMetadataRow(metadata), _metadataOnly: true } as unknown as SiteRagReport;
}
